/**
 * TurnGate — the ONE place that decides "this is a user turn" in a voice call (duplex v3 §5): hold, merge, dedupe.
 * STT finals in; `onTurn({id, text, replaces?})` out, the text always the user's words verbatim (a merge joins finals).
 *  - a final that ends mid-phrase ("and then the…") is HELD `holdMs`: more speech merges into one turn, silence sends it;
 *  - an identical final within `dupWindowMs` is dropped (#922: duplicate user turns);
 *  - a fuller re-final (same sttId, or a word-prefix extension within the window) of a turn whose reply has NOT started
 *    replaces it (`replaces` = the old id; the host rolls that turn back); after the reply started, only its new words go;
 *  - one goodbye: once a closing turn went out, a further closing turn is dropped.
 */
import { norm } from './channelLang';

/** Words after which a spoken request cannot have ended — spelled-out path punctuation ("under Projects, slash") and
 *  connectives/prepositions/articles. A reflex dispatch on such a fragment re-dispatches on every following fragment
 *  (live 20261013-085238 t5→t6→t7). */
const MID_PHRASE_WORDS = new Set([
  'slash', 'backslash', 'dash', 'hyphen', 'dot', 'underscore', 'colon', 'comma',
  'and', 'or', 'but', 'so', 'because', 'if', 'then', 'called', 'named', 'the', 'a', 'an', 'my', 'your', 'our', 'their',
  'is', 'are', 'um', 'uh',
  // Hebrew connectives/prepositions a request cannot end on ("תפתח את", "ואז")
  'של', 'את', 'עם', 'על', 'אבל', 'או', 'כי', 'אם', 'ואז', 'וגם', 'כמו', 'לגבי', 'בשביל', 'אל', 'עד', 'אחרי', 'לפני', 'שזה', 'אה', 'אממ',
]);
/** Prepositions end a fragment ("Open the file in") but also end complete requests: questions ("What are you working
 *  on", "Who is it for") and phrasal verbs on a pronoun/particle ("Turn it on", "up to") — those don't count. */
const TRAILING_PREPS = new Set(['to', 'for', 'with', 'about', 'of', 'at', 'in', 'on', 'from', 'by', 'into', 'under', 'inside']);
const QUESTION_START = /^(?:what|who|whom|whose|where|when|why|how|which|is|are|was|were|do|does|did|can|could|will|would|should|have|has)\b/i;
const PHRASAL_HOST = new Set(['it', 'this', 'that', 'them', 'him', 'her', 'me', 'us', 'you', 'up', 'out', 'off', 'down', 'back', 'over', 'away']);
/** Does the user's line stop mid-phrase (trailing connective / spelled punctuation / comma / slash)? Only the user's own
 *  words count — host notes appended after a newline ("[the user interrupted you…]") are ignored. */
export function endsMidPhrase(text: string): boolean {
  const t = text.split('\n[')[0].trim();
  if (!t || /[.?!…]["')\]]*$/.test(t)) return false;
  if (/\b[a-z][\w+.-]*:\/\/\S+$/i.test(t)) return false; // a URL's own slash/colon is not spelled punctuation
  if (/[,/\\:\-–—]$/.test(t)) return true;
  const [last, prev] = (t.toLowerCase().match(/[\p{L}']+/gu) ?? []).reverse();
  if (!last) return false;
  if (TRAILING_PREPS.has(last)) return !QUESTION_START.test(t) && !PHRASAL_HOST.has(prev);
  return MID_PHRASE_WORDS.has(last);
}

const words = (s: string): string[] => (s.trim() ? s.trim().split(/\s+/) : []);

/** A short closing line ("ok bye", "thanks, see you", "ביי", "להתראות"). Long lines that merely contain "bye" are not. */
export function isClosing(text: string): boolean {
  return words(text).length <= 8 && /(?:^|[^\p{L}])(?:good ?bye|bye|see you|good ?night|talk (?:to you )?later|hang up|end (?:the )?call|ביי|להתראות|נתראה|לילה טוב|יאללה ביי)(?:$|[^\p{L}])/iu.test(text);
}

export interface GateTurn {
  id: string;
  /** The user's words, verbatim (several finals joined by a space when merged). */
  text: string;
  /** The turn this one replaces (its reply had not started): the host rolls it back. */
  replaces?: string;
  /** new = a fresh turn · replace = a fuller re-final of the last · extend = only the words a re-final added after the reply started. */
  kind: 'new' | 'replace' | 'extend';
}
export type GateDrop = 'empty' | 'dupe' | 'stale' | 'goodbye';

export class TurnGateOptions {
  holdMs = 1200;
  /** An identical final within this window is a duplicate. */
  dupWindowMs = 5000;
  /** A fuller final within this window of the last turn (or with its sttId) is a re-final of it. */
  refinalWindowMs = 5000;
  onTurn: (t: GateTurn) => void = () => {};
  onDrop: (text: string, why: GateDrop) => void = () => {};
  /** A final is being held (ends mid-phrase) — the fill channel may cover it with a filler, never a bridge. */
  onHold: (text: string) => void = () => {};
  now: () => number = () => performance.now();
}

interface Sent { id: string; text: string; key: string; sttId?: string; at: number; started: boolean }

export class TurnGate {
  private last?: Sent;
  private held?: { text: string; sttId?: string; timer: ReturnType<typeof setTimeout> };
  private seq = 0;
  private closed = false;

  public constructor(public options?: Partial<TurnGateOptions>) {
    this.options = { ...new TurnGateOptions(), ...options };
  }
  private get o(): TurnGateOptions { return this.options as TurnGateOptions; }

  /** A closing turn went out — the call is ending. */
  get ending(): boolean { return this.closed; }
  /** Is a final held right now? */
  get holding(): boolean { return !!this.held; }
  /** The last turn sent (id + text), if any. */
  get lastTurn(): { id: string; text: string; started: boolean } | undefined { return this.last && { id: this.last.id, text: this.last.text, started: this.last.started }; }

  /** An STT final. */
  final(text: string, sttId?: string): void {
    let t = text.trim();
    if (!t) return this.o.onDrop(text, 'empty');
    if (this.held) { // more speech after a held fragment: one turn (a re-final of the held text replaces it)
      const h = this.held; clearTimeout(h.timer); this.held = undefined;
      if (!(norm(t) + ' ').startsWith(norm(h.text) + ' ')) t = `${h.text} ${t}`;
      sttId ??= h.sttId;
    }
    const key = norm(t), now = this.o.now(), last = this.last;
    if (last) {
      const age = now - last.at;
      if (key === last.key && age < this.o.dupWindowMs) return this.o.onDrop(t, 'dupe');
      const sameStt = !!sttId && sttId === last.sttId;
      if ((sameStt || age < this.o.refinalWindowMs) && key && (last.key + ' ').startsWith(key + ' ')) return this.o.onDrop(t, 'stale'); // a shorter re-final of what went out
      if (sameStt || (age < this.o.refinalWindowMs && key.startsWith(last.key + ' '))) return this.refinal(last, t, key, sttId);
    }
    if (this.closed && isClosing(t)) return this.o.onDrop(t, 'goodbye');
    if (endsMidPhrase(t)) {
      this.held = { text: t, sttId, timer: setTimeout(() => this.flush(), this.o.holdMs) };
      return this.o.onHold(t);
    }
    this.send(t, key, sttId, 'new');
  }

  /** The user is speaking again (a partial): a held fragment waits for the rest. */
  speaking(): void {
    if (!this.held) return;
    clearTimeout(this.held.timer);
    this.held.timer = setTimeout(() => this.flush(), this.o.holdMs);
  }

  /** MAIN started replying to turn `id` (its first text/tool/audio): a later re-final only adds its new words. */
  replyStarted(id: string): void { if (this.last?.id === id) this.last.started = true; }

  /** Send a held fragment now (e.g. the call ends). */
  flush(): void {
    const h = this.held; if (!h) return;
    clearTimeout(h.timer); this.held = undefined;
    if (this.closed && isClosing(h.text)) return this.o.onDrop(h.text, 'goodbye');
    this.send(h.text, norm(h.text), h.sttId, 'new');
  }

  end(): void { if (this.held) { clearTimeout(this.held.timer); this.held = undefined; } }

  private refinal(last: Sent, t: string, key: string, sttId?: string): void {
    if (!last.started) return this.send(t, key, sttId ?? last.sttId, 'replace', last.id);
    // the reply is under way: only the words beyond what it already answers (by word-level common prefix)
    const a = words(last.text), b = words(t);
    let i = 0; while (i < a.length && i < b.length && norm(a[i]) === norm(b[i])) i++;
    const extra = b.slice(i).join(' ');
    if (!norm(extra)) return this.o.onDrop(t, 'stale');
    if (this.closed && isClosing(extra)) return this.o.onDrop(t, 'goodbye'); // "…bye. Goodbye." after the goodbye went out
    this.last = { ...last, text: t, key, at: this.o.now() }; // later re-finals compare against the whole line
    this.emit(extra, 'extend');
  }

  private send(t: string, key: string, sttId: string | undefined, kind: GateTurn['kind'], replaces?: string): void {
    const id = this.emit(t, kind, replaces);
    this.last = { id, text: t, key, sttId, at: this.o.now(), started: false };
  }

  private emit(text: string, kind: GateTurn['kind'], replaces?: string): string {
    const id = `u${++this.seq}`;
    if (isClosing(text)) this.closed = true;
    if (this.last && kind === 'extend') this.last.id = id; // still 'started': the whole line keeps counting as answered
    this.o.onTurn({ id, text, kind, ...(replaces ? { replaces } : {}) });
    return id;
  }
}
